/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import {
  // ── Navigation & Layout ──────────────────────────────────────────────────
  Activity, ArrowDown, ArrowDownRight, ArrowLeft, ArrowLeftFromLine, ArrowRight, ArrowRightFromLine,
  ArrowUp, ArrowUpCircle, ArrowUpDown, ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
   
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Expand, GripVertical, Home, Layout, LayoutDashboard, LayoutGrid, LayoutList, List, Maximize2, Menu,
  Minimize2, Palette, PanelRightClose, Rows3, Shrink, SlidersHorizontal, Sliders,

  // ── Actions ───────────────────────────────────────────────────────────────
  BadgeCheck, Brain, Check, CheckCheck, Command, Copy, Download, Edit, Edit2, Edit3, ExternalLink,
  Filter, Handshake, LogOut, Pen, Pencil, Plus, RefreshCcw, RefreshCw, Reply, RotateCcw,
  Save, Search, Send, Settings, Settings2, Share, Share2, Shuffle, Trash2,
  Upload, Wrench, X,

  // ── Status & Feedback ─────────────────────────────────────────────────────
  AlertCircle, AlertOctagon, AlertTriangle, CheckCircle, CheckCircle2,
  HelpCircle, Info, Loader, Loader2, MinusCircle, XCircle, XOctagon,

  // ── Status & Other ────────────────────────────────────────────────────────
  Award, Ban, Beaker, Bookmark, Bug, CalendarRange, Fingerprint, GitCompare, Hash, Moon,
  MemoryStick, Music, Network, Package, PlayCircle, Printer, Sun, TimerOff, Trophy, UserCheck, UserX, Zap,

  // ── Objects & Content ─────────────────────────────────────────────────────
  Archive, AtSign, Banknote, Bell, BellOff, BookOpen, Briefcase, Building, Building2,
  Calculator, Calendar, CircleDollarSign, Clipboard, Compass,
  ClipboardCheck, Clock, Cloud, CloudOff, Coins, CreditCard, Crown, Database, DollarSign, Eye, EyeOff,
  Expand as ExpandIcon,
  File, FileAudio, FileCheck, FileCode, FileDown, FileEdit, FileJson, FileJson2, FileQuestion, FileSpreadsheet, FileText,
  FileVideo, FileWarning, FileX,
  Flag, Flame, Folder,
  FolderArchive, FolderInput, FolderKanban, FolderOpen, FolderPlus, FolderX, Globe, Grid, HardDrive, Heart, History, Inbox,
  Key, KeyRound, Keyboard, Laptop, Layers, LifeBuoy, Lightbulb, Link, Link2, Link2Off, ListChecks,
  ListTodo, Lock, Mail, MapPin, MessageCircle, MessageSquare, Mic,
  Minus, Monitor, MoreHorizontal, MoreVertical, Paperclip, Pause, Percent,
  Phone, PhoneIncoming, PhoneMissed, PhoneOff, PieChart, Pin, Play, Radio,
  Receipt, Repeat, Rocket, Scale, Server, Shield, ShieldAlert, ShieldCheck,
  ShieldOff, ShieldQuestion, ShieldX, Slash, Smartphone, Smile, Sparkles, Square, Stamp, Star,
  StickyNote, Table, Tag, Target, Terminal, Timer, ToggleLeft, ToggleRight, TrendingDown,
   
  TrendingUp, Undo2, Unlink, Unlock, Webhook,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Upload as UploadLucide, Image, User, UserCog, UserMinus, UserPlus, Users, Users2, Vault, Video,
  VideoOff, Volume2, VolumeX, Wifi, WifiOff,

  // ── Charts & Data ─────────────────────────────────────────────────────────
  BarChart2, BarChart3, Gauge, GitBranch, GitMerge, LineChart,
  CheckSquare, Circle, CircleDot, CornerDownLeft, Cpu,

  // ── Social ────────────────────────────────────────────────────────────────
   
  Github, Linkedin, Twitter,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Icon, type IconProps, type IconIntent } from './Icon';
import AX1Logo from './AX1Logo';
import { UploadCloud } from './Icon';

// ═══════════════════════════════════════════════════════════════════════════════
// UNIFIED ICON REGISTRY — AX1 Governance Layer
// ═══════════════════════════════════════════════════════════════════════════════
//
// ALL icon usage in page/feature code MUST go through <AppIcon name="..." />.
// Direct lucide-react imports in pages are blocked by ESLint.
//
// Naming convention: camelCase semantic names.
// Legacy kebab-case aliases retained for backward compatibility.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * All available icon names in the AX1 icon system.
 *
 * Semantic aliases (e.g. `dashboard`, `project`, `task`) are preferred.
 * Lucide-style names (e.g. `layoutDashboard`, `folderOpen`) also available.
 */
export type IconName =
  // ── Semantic aliases (preferred) ──────────────────────────────────────────
  | 'dashboard' | 'project' | 'task' | 'milestone' | 'company' | 'document' | 'user'
  | 'users' | 'finance' | 'security' | 'success' | 'warning' | 'error'
  | 'investment' | 'governance' | 'decision' | 'role' | 'approval'
  | 'dataRoom' | 'report' | 'capital' | 'vault' | 'evidence' | 'chat'
  | 'notification' | 'verification' | 'industry' | 'department'
  | 'documentFolder' | 'team' | 'escalation'
  | 'edit' | 'delete' | 'download' | 'upload' | 'search' | 'close'
  | 'chevronDown' | 'chevronRight' | 'chevronLeft' | 'chevronUp'
  | 'plus' | 'clock' | 'calendar' | 'settings' | 'home' | 'menu'
  | 'filter' | 'check' | 'info' | 'help' | 'bell' | 'mail'
  | 'lock' | 'unlock' | 'eye' | 'eyeOff' | 'copy' | 'save'
  | 'send' | 'reply' | 'share' | 'link' | 'unlink' | 'pin'
  | 'star' | 'heart' | 'flag' | 'tag' | 'target'
  | 'play' | 'pause' | 'stop' | 'refresh' | 'logout'
  | 'arrowUp' | 'arrowDown' | 'arrowLeft' | 'arrowRight'

  // ── Lucide camelCase names ────────────────────────────────────────────────
  | 'activity' | 'alertCircle' | 'alertTriangle' | 'alertOctagon'
  | 'archive' | 'arrowDownRight' | 'arrowLeftFromLine' | 'arrowRightFromLine' | 'arrowUpDown'
  | 'atSign' | 'banknote' | 'barChart2' | 'barChart3' | 'bellOff' | 'bookOpen' | 'briefcase'
  | 'brain' | 'building' | 'building2' | 'calculator' | 'checkCheck' | 'checkCircle' | 'checkCircle2'
  | 'checkSquare' | 'circle' | 'circleDollarSign' | 'circleDot' | 'clipboard' | 'clipboardCheck'
  | 'cloud' | 'cloudOff' | 'coins' | 'compass' | 'cornerDownLeft' | 'cpu' | 'creditCard' | 'crown' | 'database'
  | 'dollarSign' | 'edit2' | 'edit3' | 'expand' | 'externalLink' | 'file'
  | 'fileAudio' | 'fileCheck' | 'fileCode' | 'fileDown' | 'fileJson' | 'fileJson2' | 'fileQuestion' | 'fileSpreadsheet'
  | 'fileEdit' | 'fileText' | 'fileVideo' | 'fileWarning' | 'fileX' | 'flame'
  | 'folder' | 'folderArchive' | 'folderInput' | 'folderKanban' | 'folderOpen' | 'folderPlus' | 'folderX'
  | 'gauge' | 'gitBranch' | 'gitMerge' | 'globe' | 'grid'
  | 'gripVertical' | 'hardDrive' | 'helpCircle' | 'history'
  | 'key' | 'keyRound' | 'keyboard' | 'laptop' | 'layers' | 'layoutDashboard' | 'layoutGrid'
  | 'layoutList' | 'lifeBuoy' | 'lightbulb' | 'lineChart' | 'link2' | 'link2Off' | 'list'
  | 'listChecks' | 'listTodo' | 'loader' | 'loader2'
  | 'mapPin' | 'maximize2' | 'messageCircle' | 'messageSquare'
  | 'mic' | 'minimize2' | 'minus' | 'minusCircle'
  | 'monitor' | 'moreHorizontal' | 'moreVertical'
  | 'palette' | 'panelRightClose' | 'paperclip' | 'pencil' | 'percent'
  | 'phone' | 'phoneIncoming'
  | 'phoneMissed' | 'phoneOff' | 'pieChart' | 'radio' | 'receipt'
  | 'refreshCcw' | 'refreshCw' | 'rocket' | 'rotateCcw' | 'rows3'
  | 'repeat' | 'scale' | 'server' | 'settings2' | 'share2' | 'shield' | 'shieldAlert'
  | 'shieldCheck' | 'shieldOff' | 'shieldQuestion' | 'shieldX' | 'shrink' | 'shuffle'
  | 'slash' | 'sliders' | 'slidersHorizontal' | 'smartphone' | 'smile' | 'sparkles'
  | 'image'
  | 'arrowUpCircle' | 'arrowUpRight' | 'award' | 'badgeCheck' | 'ban' | 'beaker' | 'bookmark' | 'bug'
  | 'calendarRange' | 'command' | 'fileArchive' | 'fileBox' | 'fingerprint' | 'gitCompare' | 'handshake'
  | 'hash' | 'layout' | 'logOut' | 'memoryStick' | 'moon' | 'music' | 'network'
  | 'package' | 'pen' | 'playCircle' | 'printer' | 'sun' | 'timerOff' | 'trophy'
  | 'userX' | 'wrench' | 'zap'
  | 'inbox'
  | 'square' | 'stickyNote' | 'table' | 'toggleLeft' | 'toggleRight'
  | 'terminal' | 'timer' | 'trash2' | 'trendingDown'
  | 'trendingUp' | 'undo2' | 'uploadCloud' | 'user' | 'userCheck' | 'userCog' | 'userMinus' | 'userPlus' | 'users2'
  | 'stamp'
  | 'vault' | 'video' | 'videoOff' | 'volume2' | 'volumeX'
  | 'webhook' | 'wifi' | 'wifiOff' | 'x' | 'xCircle' | 'xOctagon'

  // ── Social ────────────────────────────────────────────────────────────────
  | 'github' | 'twitter' | 'linkedin'

  // ── Brand ─────────────────────────────────────────────────────────────────
  | 'ax1Logo' | 'infinityLogo'

  // ── Legacy kebab-case aliases (backward compat) ───────────────────────────
  | 'alert-circle' | 'alert-triangle' | 'alert-octagon'
  | 'arrow-left' | 'arrow-right' | 'arrow-up' | 'arrow-down'
  | 'bar-chart-2' | 'bar-chart-3' | 'building-2' | 'check-circle'
  | 'check-square' | 'chevron-down' | 'chevron-left' | 'chevron-right'
  | 'chevron-up' | 'dollar-sign' | 'eye-off' | 'external-link'
  | 'file-text' | 'help-circle' | 'line-chart' | 'more-horizontal'
  | 'more-vertical' | 'refresh-cw' | 'share-2' | 'trash-2'
  | 'upload-cloud' | 'user-plus' | 'x-circle'
  | 'ax1-logo' | 'infinity-logo' | 'message-circle' | 'message-square';

/**
 * Master icon map — single source of truth for all icon bindings.
 */
const iconMap: Record<IconName, React.FC<React.SVGProps<SVGSVGElement>>> = {
  // ── Semantic aliases (preferred in page code) ─────────────────────────────
  dashboard: LayoutDashboard,
  project: FolderOpen,
  task: CheckSquare,
  milestone: Flag,
  company: Building2,
  document: FileText,
  documentFolder: Folder,
  investment: Briefcase,
  governance: Scale,
  decision: Scale,
  role: UserCog,
  approval: Stamp,
  dataRoom: Package,
  report: FileText,
  finance: DollarSign,
  capital: CircleDollarSign,
  vault: Vault,
  evidence: FileCheck,
  chat: MessageSquare,
  notification: Bell,
  verification: ShieldCheck,
  industry: Building,
  department: Layers,
  team: Users,
  escalation: AlertTriangle,
  security: Shield,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  delete: Trash2,
  close: X,
  stop: Square,
  help: HelpCircle,
  logout: LogOut,
  refresh: RefreshCw,

  // ── Lucide camelCase ──────────────────────────────────────────────────────
  activity: Activity,
  alertCircle: AlertCircle,
  alertTriangle: AlertTriangle,
  alertOctagon: AlertOctagon,
  archive: Archive,
  arrowDown: ArrowDown,
  arrowDownRight: ArrowDownRight,
  arrowLeft: ArrowLeft,
  arrowLeftFromLine: ArrowLeftFromLine,
  arrowRight: ArrowRight,
  arrowRightFromLine: ArrowRightFromLine,
  arrowUp: ArrowUp,
  arrowUpCircle: ArrowUpCircle,
  arrowUpDown: ArrowUpDown,
  arrowUpRight: ArrowUpRight,
  atSign: AtSign,
  award: Award,
  badgeCheck: BadgeCheck,
  ban: Ban,
  banknote: Banknote,
  beaker: Beaker,
  bookmark: Bookmark,
  barChart2: BarChart2,
  barChart3: BarChart3,
  bell: Bell,
  bellOff: BellOff,
  bookOpen: BookOpen,
  briefcase: Briefcase,
  brain: Brain,
  bug: Bug,
  building: Building,
  building2: Building2,
  calculator: Calculator,
  calendar: Calendar,
  calendarRange: CalendarRange,
  check: Check,
  checkCheck: CheckCheck,
  checkCircle: CheckCircle,
  checkCircle2: CheckCircle2,
  checkSquare: CheckSquare,
  chevronDown: ChevronDown,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  chevronUp: ChevronUp,
  circle: Circle,
  circleDollarSign: CircleDollarSign,
  circleDot: CircleDot,
  clipboard: Clipboard,
  clipboardCheck: ClipboardCheck,
  clock: Clock,
  cloud: Cloud,
  cloudOff: CloudOff,
  coins: Coins,
  command: Command,
  compass: Compass,
  copy: Copy,
  creditCard: CreditCard,
  cornerDownLeft: CornerDownLeft,
  cpu: Cpu,
  crown: Crown,
  database: Database,
  dollarSign: DollarSign,
  download: Download,
  edit: Edit,
  edit2: Edit2,
  edit3: Edit3,
  externalLink: ExternalLink,
  expand: ExpandIcon,
  eye: Eye,
  eyeOff: EyeOff,
  file: File,
  fileAudio: FileAudio,
  fileCheck: FileCheck,
  fileCode: FileCode,
  fileDown: FileDown,
  fileEdit: FileEdit,
  fileJson: FileJson,
  fileJson2: FileJson2,
  fileQuestion: FileQuestion,
  fileSpreadsheet: FileSpreadsheet,
  fileText: FileText,
  fileVideo: FileVideo,
  fileWarning: FileWarning,
  fileX: FileX,
  fileArchive: FolderArchive,
  fileBox: Package,
  filter: Filter,
  flag: Flag,
  flame: Flame,
  folder: Folder,
  folderArchive: FolderArchive,
  folderInput: FolderInput,
  folderKanban: FolderKanban,
  folderOpen: FolderOpen,
  folderPlus: FolderPlus,
  folderX: FolderX,
  gauge: Gauge,
  gitBranch: GitBranch,
  gitCompare: GitCompare,
  gitMerge: GitMerge,
  globe: Globe,
  grid: Grid,
  gripVertical: GripVertical,
  hardDrive: HardDrive,
  handshake: Handshake,
  hash: Hash,
  heart: Heart,
  helpCircle: HelpCircle,
  history: History,
  home: Home,
  info: Info,
  fingerprint: Fingerprint,
  key: Key,
  keyRound: KeyRound,
  keyboard: Keyboard,
  laptop: Laptop,
  layers: Layers,
  layout: Layout,
  layoutDashboard: LayoutDashboard,
  layoutGrid: LayoutGrid,
  layoutList: LayoutList,
  lifeBuoy: LifeBuoy,
  lightbulb: Lightbulb,
  lineChart: LineChart,
  link: Link,
  link2: Link2,
  link2Off: Link2Off,
  list: List,
  listChecks: ListChecks,
  listTodo: ListTodo,
  loader: Loader,
  loader2: Loader2,
  lock: Lock,
  logOut: LogOut,
  mail: Mail,
  mapPin: MapPin,
  maximize2: Maximize2,
  menu: Menu,
  messageCircle: MessageCircle,
  messageSquare: MessageSquare,
  mic: Mic,
  minimize2: Minimize2,
  minus: Minus,
  minusCircle: MinusCircle,
  monitor: Monitor,
  memoryStick: MemoryStick,
  moon: Moon,
  music: Music,
  network: Network,
  moreHorizontal: MoreHorizontal,
  moreVertical: MoreVertical,
  package: Package,
  palette: Palette,
  panelRightClose: PanelRightClose,
  paperclip: Paperclip,
  pause: Pause,
  pen: Pen,
  pencil: Pencil,
  percent: Percent,
  phone: Phone,
  phoneIncoming: PhoneIncoming,
  phoneMissed: PhoneMissed,
  phoneOff: PhoneOff,
  pieChart: PieChart,
  pin: Pin,
  play: Play,
  playCircle: PlayCircle,
  plus: Plus,
  printer: Printer,
  radio: Radio,
  receipt: Receipt,
  repeat: Repeat,
  refreshCcw: RefreshCcw,
  refreshCw: RefreshCw,
  reply: Reply,
  rocket: Rocket,
  rotateCcw: RotateCcw,
  rows3: Rows3,
  save: Save,
  scale: Scale,
  search: Search,
  send: Send,
  server: Server,
  settings: Settings,
  settings2: Settings2,
  share: Share,
  share2: Share2,
  shield: Shield,
  shieldAlert: ShieldAlert,
  shieldCheck: ShieldCheck,
  shieldOff: ShieldOff,
  shieldQuestion: ShieldQuestion,
  shieldX: ShieldX,
  stamp: Stamp,
  shrink: Shrink,
  shuffle: Shuffle,
  slash: Slash,
  sliders: Sliders,
  slidersHorizontal: SlidersHorizontal,
  smartphone: Smartphone,
  smile: Smile,
  sparkles: Sparkles,
  image: Image,
  inbox: Inbox,
  square: Square,
  star: Star,
  stickyNote: StickyNote,
  sun: Sun,
  table: Table,
  tag: Tag,
  target: Target,
  terminal: Terminal,
  timer: Timer,
  timerOff: TimerOff,
  toggleLeft: ToggleLeft,
  toggleRight: ToggleRight,
  trash2: Trash2,
  trendingDown: TrendingDown,
  trendingUp: TrendingUp,
  trophy: Trophy,
  undo2: Undo2,
  unlink: Unlink,
  unlock: Unlock,
  upload: Upload,
  uploadCloud: UploadCloud,
  user: User,
  userCheck: UserCheck,
  userCog: UserCog,
  userMinus: UserMinus,
  userPlus: UserPlus,
  users: Users,
  userX: UserX,
  users2: Users2,
  video: Video,
  videoOff: VideoOff,
  volume2: Volume2,
  volumeX: VolumeX,
  webhook: Webhook,
  wifi: Wifi,
  wifiOff: WifiOff,
  wrench: Wrench,
  x: X,
  xCircle: XCircle,
  xOctagon: XOctagon,
  zap: Zap,

  // ── Social ────────────────────────────────────────────────────────────────
  github: Github,
  twitter: Twitter,
  linkedin: Linkedin,

  // ── Brand ─────────────────────────────────────────────────────────────────
  ax1Logo: AX1Logo,
  infinityLogo: AX1Logo,  // legacy alias

  // ── Legacy kebab-case aliases (backward compat) ───────────────────────────
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'alert-octagon': AlertOctagon,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'bar-chart-2': BarChart2,
  'bar-chart-3': BarChart3,
  'building-2': Building2,
  'check-circle': CheckCircle,
  'check-square': CheckSquare,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'dollar-sign': DollarSign,
  'eye-off': EyeOff,
  'external-link': ExternalLink,
  'file-text': FileText,
  'help-circle': HelpCircle,
  'line-chart': LineChart,
  'more-horizontal': MoreHorizontal,
  'more-vertical': MoreVertical,
  'refresh-cw': RefreshCw,
  'share-2': Share2,
  'trash-2': Trash2,
  'upload-cloud': UploadCloud,
  'user-plus': UserPlus,
  'x-circle': XCircle,
  'ax1-logo': AX1Logo,
  'infinity-logo': AX1Logo,  // legacy alias
  'message-circle': MessageCircle,
  'message-square': MessageSquare,
};

/**
 * Retrieve the raw Lucide component for a registered name.
 * Useful when a third-party component requires a component ref.
 */
export const getIconComponent = (name: IconName): React.FC<React.SVGProps<SVGSVGElement>> | undefined =>
  iconMap[name];

/**
 * Props for the governed AppIcon component.
 */
export interface AppIconProps extends Omit<IconProps, 'icon'> {
  /** Governed icon name from the registry */
  name: IconName;
}

/**
 * Governed application icon — the primary entry point for all icon usage
 * in page and feature code.
 *
 * @example
 * ```tsx
 * // Semantic name
 * <AppIcon name="dashboard" size="md" intent="active" />
 *
 * // Lucide camelCase name
 * <AppIcon name="chevronDown" size="sm" intent="muted" />
 *
 * // Standalone accessible icon
 * <AppIcon name="delete" size="sm" intent="danger" ariaLabel="Delete item" />
 * ```
 */
export const AppIcon: React.FC<AppIconProps> = ({ name, ...props }) => {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`[AppIcon] "${name}" is not in the icon registry. Add it to IconRegistry.tsx.`);
    }
    return null;
  }

  return <Icon icon={IconComponent} {...props} />;
};
